import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useCredits } from "../context/CreditsContext";

export function useWorkspace(id) {
  const [project, setProject] = useState(null);
  const [version, setVersion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [stopping, setStopping] = useState(false);
  const { refresh: refreshCredits } = useCredits();

  const loadProject = useCallback(async () => {
    const r = await api.project(id);
    setProject(r.project);
    setVersion(r.version);
  }, [id]);

  useEffect(() => {
    Promise.all([loadProject(), api.messages(id).then(setMessages)]).catch((e) => setError(errMsg(e, "Project not found")));
  }, [id, loadProject]);

  const streamingId = messages.find((m) => m.status === "streaming")?.id;

  useEffect(() => {
    if (!streamingId) return undefined;
    let alive = true;
    const t = setInterval(async () => {
      try {
        const m = await api.message(id, streamingId);
        if (!alive) return;
        setMessages((ms) => ms.map((x) => (x.id === m.id ? m : x)));
        if (m.status !== "streaming") {
          clearInterval(t);
          await loadProject();
          refreshCredits();
          setStopping(false);
          if (m.status === "done" && m.kind === "plan") toast.success("Plan ready — answer the questions, then build it");
          else if (m.status === "done") toast.success(m.version_number ? `Version ${m.version_number} is ready` : "Reply ready");
          else if (m.status === "stopped") toast("Build stopped — your credits were refunded");
          else toast.error("Build failed — your credits were refunded");
        }
      } catch { /* keep polling */ }
    }, 900);
    return () => { alive = false; clearInterval(t); };
  }, [id, streamingId, loadProject, refreshCredits]);

  const send = useCallback(async (text, model, opts = {}) => {
    try {
      const r = await api.sendMessage(id, text, model, opts);
      setMessages((ms) => [...ms, r.user_message, r.assistant_message]);
      setProject((p) => ({ ...p, status: "generating" }));
      refreshCredits();
    } catch (e) {
      toast.error(errMsg(e, "Could not send"));
    }
  }, [id, refreshCredits]);

  const stop = useCallback(async () => {
    setStopping(true);
    try { await api.stopBuild(id); } catch (e) { setStopping(false); toast.error(errMsg(e, "Could not stop")); }
  }, [id]);

  return { project, setProject, version, messages, building: !!streamingId, stopping, send, stop, loadProject, error };
}
